export interface FeedItem {
  time: string;
  tag: '线索' | '实锤' | '反转' | '复盘';
  title: string;
  summary: string;
  badge: string;
}

export interface TimelineStep {
  date: string;
  title: string;
  text: string;
  mark: string;
}

export interface EvidenceItem {
  code: string;
  label: string;
  status: '已核验' | '待复核' | '存疑';
  title: string;
  text: string;
}

export interface ReviewItem {
  tag: string;
  title: string;
  summary: string;
  phase: string;
  image: string;
  alt: string;
}

export interface ClueTopic {
  name: string;
  count: number;
  hot: boolean;
}

export const feedItems: FeedItem[] = [
  {
    time: '12:40',
    tag: '线索',
    title: '新线索 #0527 浮出，指向一处未披露的时间戳',
    summary: '网友整理的首条横向对照，补上了此前缺失的空窗段，正等待第二来源交叉印证。',
    badge: '追踪中',
  },
  {
    time: '11:05',
    tag: '实锤',
    title: '关键原帖截图完成核验，口径与原始文档一致',
    summary: '时间、账号与正文逐字比对通过，该条已从"疑似"升级为"可引用"级别。',
    badge: '已核验',
  },
  {
    time: '09:32',
    tag: '反转',
    title: '此前流传的剪辑片段被证存在断章，口径需修正',
    summary: '补全前后文后结论发生偏移，相关二手转发已标注需以完整版本为准。',
    badge: '已修正',
  },
  {
    time: '昨日 21:17',
    tag: '复盘',
    title: '#0409 事件首轮回溯已归档，重点分歧全部标注',
    summary: '复盘文档以时间线方式逐条列出证据与来源，开放二次讨论与补充。',
    badge: '已归档',
  },
];

export const timelineSteps: TimelineStep[] = [
  {
    date: '第 1 日',
    title: '首条线索出现',
    text: '单一账号抛出异常截图，信息零散且无来源，社区保持观望，未做任何定性。',
    mark: '线索',
  },
  {
    date: '第 3 日',
    title: '来源开始交叉',
    text: '多位独立观察者给出相近时间戳，但口径存在细微出入，进入横向比对阶段。',
    mark: '交叉',
  },
  {
    date: '第 6 日',
    title: '关键实锤落定',
    text: '原始文档与账号后台可对照，关键节点完成核验，讨论焦点转向细节解释。',
    mark: '实锤',
  },
  {
    date: '第 9 日',
    title: '出现首次反转',
    text: '补全上下文后，部分二手转述被证断章，主流叙事出现一次方向修正。',
    mark: '反转',
  },
  {
    date: '第 12 日',
    title: '复盘归档',
    text: '全程以时间线沉淀成复盘文档，标出证据等级与遗留分歧，供持续追踪。',
    mark: '复盘',
  },
];

export const evidenceItems: EvidenceItem[] = [
  {
    code: 'E-0127',
    label: '时间戳',
    status: '已核验',
    title: '两来源时间戳一致',
    text: 'A/B 两套独立记录在关键节点重合，无截断痕迹，作为当前最强支撑。',
  },
  {
    code: 'E-0109',
    label: '原始文档',
    status: '待复核',
    title: '正文口径待二次比对',
    text: '方向基本一致，但段落顺序存在两版差异，需要拿到原始出处定稿。',
  },
  {
    code: 'E-0088',
    label: '影像片段',
    status: '存疑',
    title: '存在断章风险',
    text: '完整版本与流传剪辑结论相反，该条暂不计入正论，仅作背景留存。',
  },
];

export const reviewItems: ReviewItem[] = [
  {
    tag: '事件复盘',
    title: '#0409 一场由单条截图引发的连锁深挖',
    summary: '从一条无人问津的帖子开始，社区如何一步步拼出完整时间线，又在何处踩了坑。',
    phase: '12 天 · 已归档',
    image: 'https://readdy.ai/api/search-image?query=editorial%20documentary%20collage%20of%20paper%20documents%20and%20red%20string%20on%20warm%20cork%20board%20tracing%20a%20timeline%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20minimal%20clean%20composition%2C%20cinematic%20shallow%20depth&width=900&height=600&seq=cg51-review-0409&orientation=landscape',
    alt: '#0409 事件复盘封面 时间线拼图',
  },
  {
    tag: '反转梳理',
    title: '#0618 一次教科书级的断章反转让全员打脸',
    summary: '所有人在第一版结论里达成共识，却漏掉了被剪掉的 40 秒，复盘误判是怎么发生的。',
    phase: '8 天 · 已归档',
    image: 'https://readdy.ai/api/search-image?query=abstract%20visual%20of%20overlapping%20media%20screens%20and%20timeline%20frames%20in%20muted%20clay%20and%20sage%20tones%2C%20clean%20editorial%20layout%2C%20soft%20shadows%2C%20minimal%20flat%20aesthetic%20with%20gentle%20paper%20texture&width=900&height=600&seq=cg51-review-0618&orientation=landscape',
    alt: '#0618 反转梳理封面 剪辑与时间轴',
  },
  {
    tag: '实锤讨论',
    title: '#0721 时间戳之争，究竟什么才算"真"',
    summary: '同一张截图在技术层面有两种解读，实锤与存疑之间，隔着的是一套核验标准。',
    phase: '追踪中',
    image: 'https://readdy.ai/api/search-image?query=editorial%20still%20life%20of%20timepieces%2C%20clocks%20and%20documents%20arranged%20minimally%20on%20warm%20desk%2C%20soft%20morning%20light%2C%20clay%20and%20cream%20palette%2C%20clean%20composition%2C%20documentary%20photography&width=900&height=600&seq=cg51-review-0721&orientation=landscape',
    alt: '#0721 实锤讨论封面 时钟与文档',
  },
  {
    tag: '线索合集',
    title: '#0813 线索档案总览：哪些待复核，哪些已闭环',
    summary: '把散落在各处的碎片归拢成一份索引，一眼看清每条线索所处阶段与下一步动作。',
    phase: '持续更新',
    image: 'https://readdy.ai/api/search-image?query=clean%20editorial%20archival%20files%20and%20index%20cards%20stacked%20on%20light%20surface%2C%20sage%20green%20and%20cream%20tones%2C%20minimal%20layout%2C%20soft%20natural%20light%2C%20flat%20documentary%20aesthetic&width=900&height=600&seq=cg51-review-0813&orientation=landscape',
    alt: '#0813 线索档案封面 索引卡片',
  },
];

export const clueTopics: ClueTopic[] = [
  { name: '#0409 时间线事件', count: 27, hot: true },
  { name: '#0618 断章争议', count: 19, hot: true },
  { name: '#0721 实锤标准', count: 15, hot: false },
  { name: '#0813 线索档案', count: 12, hot: false },
  { name: '时间戳核验', count: 9, hot: false },
  { name: '来源交叉', count: 8, hot: false },
  { name: '反转记录', count: 7, hot: false },
  { name: '复盘归档', count: 6, hot: false },
];

export const howItWorks: Array<{ icon: string; step: string; title: string; text: string }> = [
  {
    icon: 'ri-search-eye-line',
    step: '01 收集',
    title: '线索归拢',
    text: '把散落在各处的帖子、截图、时间戳统一收进一张表，先分类，再定性。',
  },
  {
    icon: 'ri-node-tree',
    step: '02 交叉',
    title: '来源互证',
    text: '坚持至少两个独立来源同时命中才升为实锤，口径不一致一律先标存疑。',
  },
  {
    icon: 'ri-history-line',
    step: '03 复盘',
    title: '归档沉淀',
    text: '每一个事件最终都沉淀为带证据等级的时间线复盘，留痕可追溯、可再讨论。',
  },
];